import React from 'react';
import { Box, Button, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ExternalLink } from '../ExternalLink';
import { useProgress } from '../../core/hooks';

export interface ProductCardProps {
  title: string;
  description: string;
  img: string;
  children: React.ReactNode;
  disabled?: boolean;
  onButtonClick: () => void;
  link: string;
  isOnlyAvailableInUK?: boolean;
  isRequest?: boolean;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  img,
  title,
  description,
  children,
  disabled = false,
  onButtonClick,
  link,
  isOnlyAvailableInUK = false,
  isRequest = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { data: progressData } = useProgress();

  // Only a live order blocks a new one. A failed order keeps its data (so the popup can show the
  // failure) but must not keep the button disabled.
  const isProcessingTrueSight =
    !isRequest && progressData?.status === 'processing' && progressData?.progress < 0.99;

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        backgroundColor: 'var(--dove-gray)',
        display: 'flex',
        flex: 1,
        borderRadius: 2,
        transition: 'box-shadow 0.3s ease',
        overflowY: 'hidden',
        ':hover': {
          boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.5)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          padding: '16px',
          overflowY: 'auto',
        }}
      >
        <Box display="flex" gap={2} marginBottom={2}>
          <img
            src={img}
            alt={title}
            style={{
              width: '100px',
              height: '100px',
              objectFit: 'cover',
              borderRadius: 10,
            }}
          />
          <Box>
            <Typography fontSize="28px" fontWeight="bold">
              {title}
            </Typography>
            <Typography
              fontSize="12px"
              color="var(--gentian-gray)"
              whiteSpace="pre-wrap"
              marginBottom="4px"
              textAlign="left"
            >
              {description}
            </Typography>
            <Typography fontSize="12px" color="var(--gentian-gray)" whiteSpace="pre-wrap">
              {isOnlyAvailableInUK ? 'Only available in UK' : 'Available Globally'}
            </Typography>
          </Box>
        </Box>
        <Box flex="1" fontSize="16px" color="var(--gentian-gray)">
          {children}
        </Box>
        <Box
          position={isMobile ? 'unset' : 'sticky'}
          textAlign="center"
          width="100%"
          bottom="-16px"
          padding="16px 0"
          left={0}
          right={0}
        >
          <Tooltip
            title={
              isProcessingTrueSight
                ? 'You have a pending project still processing, please wait for it to complete.'
                : ''
            }
            placement="top"
          >
            <span>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={onButtonClick}
                disabled={disabled || isProcessingTrueSight}
                sx={{
                  textAlign: 'center',
                  backgroundColor: 'var(--gentian-blue)', // Set the background color to --sundrop-yellow
                  color: 'var(--gentian-white)', // Set the text color to --gentian-black for contrast
                  '&:hover': {
                    backgroundColor: 'var(--eclipse-black)',
                  },
                  '&:disabled': {
                    color: 'var(--gentian-button-color-disabled)',
                    backgroundColor: 'var(--gentian-button-bg-disabled)',
                  },
                }}
              >
                {isRequest ? 'Request' : 'Buy Now'}
              </Button>
            </span>
          </Tooltip>
        </Box>
        <ExternalLink href={link}>Find Out More</ExternalLink>
      </Box>
    </Box>
  );
};
